<template>
  <div>
    <!-- Cabeçalho de contexto: escopo (Geral ou loja) das campanhas. -->
    <b-card-actions
      no-actions
      :show-loading="loading || busy"
    >
      <div class="d-flex align-items-center">
        <feather-icon
          :icon="scope === 'general' ? 'GlobeIcon' : 'HomeIcon'"
          size="22"
          class="mr-1 text-primary"
        />
        <div>
          <h5 class="mb-0">
            {{ $t('Campanhas de frete') }} — {{ scopeTitle }}
          </h5>
          <small class="text-muted">
            {{ $t('Campanhas são temporárias: valem só no período informado e substituem a configuração base nesse período.') }}
          </small>
        </div>
      </div>
    </b-card-actions>

    <!-- LISTA de campanhas -->
    <b-card-actions
      v-if="!editing"
      no-actions
      :show-loading="loading || busy"
    >
      <b-row
        align-v="center"
        class="mb-1"
      >
        <b-col md="4">
          <FormulateInput
            id="delivery-campaign-include-expired"
            v-model="includeExpiredModel"
            type="switch"
            name="includeExpired"
            :label="$t('Incluir campanhas expiradas')"
          />
        </b-col>
        <b-col class="text-right">
          <e-button
            variant="outline-primary"
            icon="plus"
            :text="$t('Nova campanha')"
            @click="newCampaign"
          />
        </b-col>
      </b-row>

      <b-table
        id="delivery-campaigns-table"
        class="table-responsive bordered"
        responsive
        striped
        show-empty
        :empty-text="$t('Nenhuma campanha cadastrada para este escopo.')"
        :items="campaignConfigs"
        :fields="fields"
      >
        <template #cell(actions)="row">
          <e-grid-actions
            :show-update="$can('Update', 'Store')"
            :show-delete="$can('Update', 'Store')"
            @update="editCampaign(row.item)"
            @delete="deleteCampaign(row.item)"
          />
        </template>
        <template #cell(period)="{ item }">
          {{ formatDate(item.effectiveFrom) }} → {{ formatDate(item.effectiveTo) }}
        </template>
        <template #cell(active)="{ value }">
          <b-badge :variant="value ? 'light-success' : 'light-secondary'">
            {{ value ? $t('Ativa') : $t('Inativa') }}
          </b-badge>
        </template>
      </b-table>
    </b-card-actions>

    <!-- EDITOR de uma campanha -->
    <FormulateForm
      v-else
      ref="form"
      name="formDeliveryCampaign"
    >
      <b-card-actions
        no-actions
        :show-loading="loading || busy"
      >
        <h5 class="mb-1">
          {{ isNew ? $t('Nova campanha') : $t('Editar campanha') }}
        </h5>
        <b-alert
          variant="info"
          show
          class="mb-1"
        >
          <div class="alert-body">
            {{ $t('O período de início e fim é obrigatório. Maior prioridade vence quando duas campanhas coincidem no mesmo período.') }}
          </div>
        </b-alert>
        <b-row>
          <b-col md="3">
            <FormulateInput
              id="delivery-campaign-from"
              v-model="config.effectiveFrom"
              type="datepicker"
              name="effectiveFrom"
              class="required"
              validation="required"
              :label="$t('Vigente de')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="delivery-campaign-to"
              v-model="config.effectiveTo"
              type="datepicker"
              name="effectiveTo"
              class="required"
              validation="required"
              :label="$t('Vigente até')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="delivery-campaign-priority"
              v-model="config.priority"
              type="text-number"
              :precision="0"
              name="priority"
              class="required"
              validation="required|min:1"
              :label="$t('Prioridade')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="delivery-campaign-active"
              v-model="config.active"
              type="switch"
              name="active"
              :label="$t('Campanha ativa')"
            />
          </b-col>
        </b-row>
      </b-card-actions>

      <b-card-actions
        no-actions
        :show-loading="loading || busy"
      >
        <b-tabs>
          <b-tab
            :title="$t('Valores da campanha')"
            active
          >
            <delivery-config-defaults :config="config" />
          </b-tab>
          <b-tab :title="$t('Ajustes por dia da semana')">
            <delivery-config-by-day :config="config" />
          </b-tab>
        </b-tabs>
      </b-card-actions>
    </FormulateForm>

    <FAB
      v-if="editing"
      :main-tooltip="$t('Ações')"
      main-icon="keyboard_command_key"
      :actions="editorActions"
      :fixed-tooltip="true"
      :busy="busy"
      @save="saveCampaign"
      @cancel="cancelEdit"
    />
    <FAB
      v-else
      :main-tooltip="$t('Voltar para a listagem')"
      main-icon="arrow_back"
      @click="goToList"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { BRow, BCol, BTabs, BTab, BAlert, BTable, BBadge } from 'bootstrap-vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import { alerts } from '@/mixins'
import { EButton, FAB } from '@/views/components'
import EGridActions from '@/views/components/EGridActions.vue'
import DeliveryConfigDefaults from '../components/DeliveryConfigDefaults.vue'
import DeliveryConfigByDay from '../components/DeliveryConfigByDay.vue'

/**
 * Tela dedicada de "Campanhas de frete" (D25/D29): gerencia as configurações TEMPORÁRIAS
 * (Priority > 0, com período de vigência) de um escopo — a Geral ou uma loja — separada da
 * configuração base (padrão). Lista as campanhas do escopo e permite criar/editar/excluir cada
 * uma, reusando os blocos de valores (`DeliveryConfigDefaults`) e ajustes por dia
 * (`DeliveryConfigByDay`). O escopo vem da tela de listagem via query (?scope=..&storeId=..).
 */
export default {
  components: {
    BRow,
    BCol,
    BTabs,
    BTab,
    BAlert,
    BTable,
    BBadge,
    BCardActions,
    EButton,
    FAB,
    EGridActions,
    DeliveryConfigDefaults,
    DeliveryConfigByDay,
  },

  mixins: [alerts],

  data() {
    return {
      loading: false,
      busy: false,
      editing: false,
    }
  },

  computed: {
    ...mapState('pages/store/deliveryConfig', ['scope', 'selectedStoreId', 'config', 'includeExpired']),
    ...mapGetters('pages/store/deliveryConfig', ['storeOptions', 'campaignConfigs']),

    isNew() {
      return !this.config.id
    },

    scopeTitle() {
      if (this.scope === 'general') {
        return this.$t('Geral (todas as lojas)')
      }
      const found = this.storeOptions.find(option => option.value === this.selectedStoreId)
      return found ? found.label : this.$t('Loja')
    },

    includeExpiredModel: {
      get() {
        return this.includeExpired
      },
      set(value) {
        this.setIncludeExpired(value)
        this.reload()
      },
    },

    fields() {
      return [
        {
          key: 'actions',
          label: this.$t('Ações'),
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          key: 'period',
          label: this.$t('Período de vigência'),
        },
        {
          key: 'priority',
          label: this.$t('Prioridade'),
          class: 'text-center',
          thStyle: { width: '110px' },
        },
        {
          key: 'active',
          label: this.$t('Status'),
          class: 'text-center',
          thStyle: { width: '100px' },
        },
      ]
    },

    editorActions() {
      return [
        { name: 'cancel', icon: 'arrow_back', color: 'red', tooltip: this.$t('Voltar') },
        { name: 'save', icon: 'save', tooltip: this.$t('Salvar') },
      ]
    },
  },

  async mounted() {
    this.loading = true
    try {
      await this.fetchStores()
      await this.fetchGroups()

      // Resolve o escopo de forma determinística a partir da query (com loja → 'store'; sem loja
      // → 'general'), sem depender do estado remanescente do módulo, evitando chamar /stores/null.
      const { scope, storeId } = this.$route.query
      let resolvedScope = scope
      if (resolvedScope !== 'store' && resolvedScope !== 'general') {
        resolvedScope = storeId ? 'store' : 'general'
      }
      this.setScope(resolvedScope)
      this.setSelectedStore(storeId ? Number(storeId) : null)

      await this.reload()
    } catch (error) {
      this.showGenericError({ error })
    } finally {
      this.loading = false
    }
  },

  beforeDestroy() {
    this.clearConfig()
  },

  methods: {
    ...mapActions('pages/store/deliveryConfig', [
      'setScope',
      'setSelectedStore',
      'setIncludeExpired',
      'fetchStores',
      'fetchGroups',
      'fetchDeliveryConfig',
      'fetchGeneralDeliveryConfig',
      'saveDeliveryConfig',
      'saveGeneralDeliveryConfig',
      'startNewCampaign',
      'deleteConfig',
      'selectConfig',
      'clearConfig',
    ]),

    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('pt-BR')
    },

    // Recarrega as configs do escopo (base + campanhas) do servidor.
    async reload() {
      // Escopo de loja sem loja selecionada é estado inconsistente: não busca (evita /stores/null).
      if (this.scope === 'store' && !this.selectedStoreId) {
        return
      }
      this.loading = true
      try {
        if (this.scope === 'store') {
          await this.fetchDeliveryConfig({ storeId: this.selectedStoreId })
        } else {
          await this.fetchGeneralDeliveryConfig()
        }
      } catch (error) {
        this.showError({ error, message: this.$t('Não foi possível carregar as campanhas') })
      } finally {
        this.loading = false
      }
    },

    newCampaign() {
      this.startNewCampaign()
      this.editing = true
    },

    editCampaign(item) {
      this.selectConfig(item.id)
      this.editing = true
    },

    cancelEdit() {
      this.editing = false
      this.reload()
    },

    goToList() {
      this.$router.push({ name: 'delivery-config-list' })
    },

    // I7: a janela de entrega de cada dia exige "Abre" e "Fecha" ambos preenchidos ou ambos vazios
    // (vazio = herda o horário da loja). Impede o erro INVALID_DELIVERY_WINDOW do backend.
    hasInvalidDeliveryWindow() {
      return (this.config.days || []).some(
        day => !!day.deliveryStartTime !== !!day.deliveryEndTime
      )
    },

    async saveCampaign() {
      this.$refs.form.showErrors()
      if (!this.$refs.form.isValid) {
        this.showInvalidDataMessage()
        return
      }
      if (this.hasInvalidDeliveryWindow()) {
        this.showInvalidDataMessage({
          message: this.$t('Em cada dia, preencha "Abre" e "Fecha" juntos, ou deixe ambos vazios para herdar o horário da loja.'),
        })
        return
      }

      try {
        this.busy = true
        if (this.scope === 'store') {
          await this.saveDeliveryConfig({ storeId: this.selectedStoreId })
        } else {
          await this.saveGeneralDeliveryConfig()
        }
        this.showSuccess({ message: this.$t('Campanha salva com sucesso') })
        this.editing = false
        await this.reload()
      } catch (error) {
        this.showError({ error, message: this.$t('Não foi possível salvar a campanha') })
      } finally {
        this.busy = false
      }
    },

    async deleteCampaign(item) {
      const confirmed = await this.confirm({
        title: this.$t('Excluir campanha?'),
        text: this.$t('Esta ação não pode ser desfeita.'),
        confirmButtonText: this.$t('Sim, excluir'),
      })
      if (!confirmed) return

      try {
        this.busy = true
        await this.deleteConfig({ configId: item.id })
        this.showSuccess({ message: this.$t('Campanha excluída com sucesso') })
        await this.reload()
      } catch (error) {
        this.showError({ error, message: this.$t('Não foi possível excluir a campanha') })
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style></style>
